import { LabelType } from "../../data/enums/LabelType";
import { EditorModel } from "../../staticModels/EditorModel";
import { RectRenderEngine } from "../render/RectRenderEngine";
import { PointRenderEngine } from "../render/PointRenderEngine";
import { PolygonRenderEngine } from "../render/PolygonRenderEngine";
import { PolylineRenderEngine } from "../render/PolylineRenderEngine";
import { LineRenderEngine } from "../render/LineRenderEngine";
import { IRect } from "../../interfaces/IRect";
import { RectUtil } from "../../utils/RectUtil";
import { EditorData } from "../../data/EditorData";
import { CanvasUtil } from "../../utils/CanvasUtil";
import React from "react";
import { IPoint } from "../../interfaces/IPoint";
import { DrawUtil } from "../../utils/DrawUtil";
import { PrimaryEditorRenderEngine } from "../render/PrimaryEditorRenderEngine";
import { ContextManager } from "../context/ContextManager";
import { PointUtil } from "../../utils/PointUtil";
import { ViewPortActions } from "./ViewPortActions";
import { ISize } from "../../interfaces/ISize";
import { ImageUtil } from "../../utils/ImageUtil";
import { GeneralSelector } from "../../store/selectors/GeneralSelector";
import { ViewPortHelper } from "../helpers/ViewPortHelper";
import { CustomCursorStyle } from "../../data/enums/CustomCursorStyle";

// =================================================================================================================
// TASK TYPE CONTROL
// =================================================================================================================

export type KnownTaskType =
  | "OBJECT_DETECTION"
  | "OBJECT_TRACKING"
  | "SEGMENTATION"
  | "KEYPOINTS"
  | "CLASSIFICATION"
  | "ALL";

export type TaskType = KnownTaskType | (string & {});

export class EditorActions {
  private static _taskType: TaskType = "ALL";

  public static setTaskType(taskType: TaskType) {
    EditorActions._taskType = taskType ?? "ALL";
  }

  public static getTaskType(): TaskType {
    return EditorActions._taskType;
  }

  public static isObjectTrackingTask(taskLike?: unknown): boolean {
    const normalized =
      taskLike === undefined
        ? EditorActions.getTaskType()
        : EditorActions.fromProjectTask(taskLike);
    return normalized === "OBJECT_TRACKING";
  }

  /** Map raw projectData.task → internal TaskType */
  public static fromProjectTask(taskLike: unknown): TaskType {
    if (!taskLike) return "ALL";
    const raw = String(taskLike).trim().toLowerCase().replace(/[\s-]+/g, "_");

    switch (raw) {
      case "object_detection":
      case "detection":
      case "bbox":
        return "OBJECT_DETECTION";
      case "object_tracking":
      case "objecttracking":
      case "Object_tracking":
      case "video_tracking":
      case "video_object_tracking":
      case "multi_object_tracking":
      case "mot":
        return "OBJECT_TRACKING";
      case "segmentation":
      case "instance_segmentation":
      case "semantic_segmentation":
        return "SEGMENTATION";
      case "keypoints":
      case "keypoint":
      case "pose_estimation":
      case "pose":
      case "pose_estimation":
      case "landmarks":
        return "KEYPOINTS";
      case "classification":
      case "image_classification":
      case "tag":
      case "tagging":
      case "image_recognition":
        return "CLASSIFICATION";
      default:
        return raw.toUpperCase();
    }
  }

  public static getDefaultLabelTypeForTask(
    taskLike: unknown,
    availableLabelTypes: LabelType[] = []
  ): LabelType {
    const raw = taskLike
      ? String(taskLike).trim().toLowerCase().replace(/[\s-]+/g, "_")
      : "";
    const normalizedTask = EditorActions.fromProjectTask(taskLike);
    const available = availableLabelTypes.filter(Boolean);

    let candidates: LabelType[] = [];

    switch (normalizedTask) {
      case "OBJECT_DETECTION":
      case "OBJECT_TRACKING":
        candidates = [LabelType.RECT];
        break;
      case "SEGMENTATION":
        candidates = [LabelType.POLYGON, LabelType.POLYLINE];
        break;
      case "KEYPOINTS":
        candidates = [LabelType.POINT];
        break;
      case "CLASSIFICATION":
        candidates = [LabelType.TAG, LabelType.IMAGE_RECOGNITION];
        break;
      default:
        if (raw.includes("polyline")) candidates = [LabelType.POLYLINE];
        else if (raw.includes("line")) candidates = [LabelType.LINE];
        break;
    }

    const preferred = candidates.find(
      (labelType) => available.length === 0 || available.includes(labelType)
    );

    return preferred || available[0] || LabelType.RECT;
  }

  // =================================================================================================================
  // ENGINE REGISTRY
  // =================================================================================================================

  private static readonly renderEngineMap: Partial<Record<LabelType, any>> = {
    [LabelType.RECT]: RectRenderEngine,
    [LabelType.POINT]: PointRenderEngine,
    [LabelType.LINE]: LineRenderEngine,
    [LabelType.POLYGON]: PolygonRenderEngine,
    [LabelType.POLYLINE]: PolylineRenderEngine,
    [LabelType.AUDIO]: RectRenderEngine,
    [LabelType.TAG]: null,
  };

  // =================================================================================================================
  // MOUNTING ENGINES
  // =================================================================================================================

  /** Mount all engines (used when taskType === ALL) */
  private static mountAllSupportRenderingEngines() {
    EditorModel.allSupportRenderingEngines?.forEach(({ engine }) => {
      if (engine instanceof RectRenderEngine) engine.dispose();
    });
    const engines = Object.entries(EditorActions.renderEngineMap)
      .filter(([_, EngineClass]) => !!EngineClass)
      .map(([type, EngineClass]) => ({
        labelType: type as LabelType,
        engine: new (EngineClass as any)(EditorModel.canvas),
      }));

    EditorModel.allSupportRenderingEngines = engines;
  }

  /** Mount one engine (used when taskType !== ALL) */
  public static mountSupportRenderingEngine(activeLabelType: LabelType) {
    if (EditorModel.supportRenderingEngine instanceof RectRenderEngine) {
      EditorModel.supportRenderingEngine.dispose();
    }
    const EngineClass = EditorActions.renderEngineMap[activeLabelType];
    if (EngineClass) {
      EditorModel.supportRenderingEngine = new EngineClass(EditorModel.canvas);
    } else {
      EditorModel.supportRenderingEngine = null;
    }
  }

  public static swapSupportRenderingEngine(activeLabelType: LabelType) {
    EditorActions.mountSupportRenderingEngine(activeLabelType);
  }

  /** Mount according to task type */
  public static mountRenderEnginesAndHelpers(activeLabelType: LabelType) {
    EditorModel.viewPortHelper = new ViewPortHelper();
    EditorModel.primaryRenderingEngine = new PrimaryEditorRenderEngine(
      EditorModel.canvas
    );

    const taskType = EditorActions.getTaskType();

    if (taskType === "ALL") {
      EditorActions.mountAllSupportRenderingEngines();
      EditorActions.mountSupportRenderingEngine(activeLabelType);
    } else {
      EditorActions.mountSupportRenderingEngine(activeLabelType);
    }
  }

  // =================================================================================================================
  // RENDER
  // =================================================================================================================

  private static pendingRenderFrame: number | null = null;
  private static pendingAfterRenderCallbacks: Array<() => void> = [];
  public static requestRender(afterRender?: () => void) {
    if (afterRender) {
      EditorActions.pendingAfterRenderCallbacks.push(afterRender);
    }
    if (EditorActions.pendingRenderFrame !== null) {
      return;
    }
    EditorActions.pendingRenderFrame = window.requestAnimationFrame(() => {
      EditorActions.pendingRenderFrame = null;
      EditorActions.fullRender();
      const callbacks = EditorActions.pendingAfterRenderCallbacks;
      EditorActions.pendingAfterRenderCallbacks = [];
      callbacks.forEach((callback) => callback());
    });
  }

  public static fullRender() {
    // 1️⃣ Always clear the main editor canvas
    DrawUtil.clearCanvas(EditorModel.canvas);
  
    const editorData = EditorActions.getEditorData();
  
    // 2️⃣ Render base image
    if (EditorModel.primaryRenderingEngine) {
      EditorModel.primaryRenderingEngine.render(editorData);
    }
  
    const taskType = EditorActions.getTaskType();
  
    if (taskType === "ALL") {
      // ✅ Find which label type corresponds to the active engine
      const activeType = Object.entries(EditorActions?.renderEngineMap)
        .find(([_, EngineClass]) =>
          typeof EngineClass === "function" &&
          !!EditorModel.supportRenderingEngine &&
          EditorModel.supportRenderingEngine instanceof (EngineClass as any)
        )?.[0] as LabelType | undefined;
    
      // ✅ Render all static annotations except the active label type
      if (EditorModel.allSupportRenderingEngines?.length) {
        for (const { labelType, engine } of EditorModel.allSupportRenderingEngines) {
          // // 🧠 Skip active type AND polyline during bbox editing to avoid duplicate artifacts
          if (labelType === activeType) continue;
        
          try {
            engine.render(editorData);
          } catch (err) {
            console.error(`Error rendering ${labelType}:`, err);
          }
        }
        
      }
      
    
      // ✅ Now draw the active engine (live editing/drawing)
      if (EditorModel.supportRenderingEngine) {
        EditorModel.supportRenderingEngine.render(editorData);
      }
    } else {
      // Normal single task rendering
      if (EditorModel.supportRenderingEngine) {
        EditorModel.supportRenderingEngine.render(editorData);
      }
    }
    
  }

  // =================================================================================================================
  // SETTERS
  // =================================================================================================================

  public static setLoadingStatus(status: boolean) {
    EditorModel.isLoading = status;
  }

  public static setActiveImage(image: HTMLImageElement) {
    EditorModel.image = image;
  }

  public static setViewPortActionsDisabledStatus(status: boolean) {
    EditorModel.viewPortActionsDisabled = status;
  }

  // =================================================================================================================
  // GETTERS
  // =================================================================================================================

  public static getEditorData(event?: Event): EditorData {
    return {
      mousePositionOnViewPortContent:
        EditorModel.mousePositionOnViewPortContent,
      viewPortContentSize: CanvasUtil.getSize(EditorModel.canvas),
      activeKeyCombo: ContextManager.getActiveCombo(),
      event,
      zoom: GeneralSelector.getZoom(),
      viewPortSize: EditorModel.viewPortSize,
      defaultRenderImageRect: EditorModel.defaultRenderImageRect,
      viewPortContentImageRect:
        ViewPortActions.calculateViewPortContentImageRect(),
      realImageSize: ImageUtil.getSize(EditorModel.image),
      absoluteViewPortContentScrollPosition:
        ViewPortActions.getAbsoluteScrollPosition(),
    };
  }

  // =================================================================================================================
  // HELPERS
  // =================================================================================================================

  public static updateMousePositionIndicator(
    event: React.MouseEvent<HTMLCanvasElement, MouseEvent> | MouseEvent
  ) {
    if (!event) {
      EditorActions.syncCursorIndicator();
      return;
    }

    // Hide custom cursor over toolbars/panels so it doesn't stack with the system cursor
    const target = event.target as Node | null;
    const isOverCanvas =
      !!EditorModel.canvas &&
      (target === EditorModel.canvas || EditorModel.canvas.contains(target));

    if (!isOverCanvas) {
      EditorActions.hideCursorIndicator();
      return;
    }

    const mousePositionOverViewPortContent: IPoint =
      CanvasUtil.getMousePositionOnCanvasFromEvent(event, EditorModel.canvas);
    EditorActions.syncCursorIndicator(mousePositionOverViewPortContent);
  }

  public static hideCursorIndicator() {
    if (EditorModel.mousePositionIndicator) {
      EditorModel.mousePositionIndicator.style.display = "none";
    }
    if (EditorModel.cursor) {
      EditorModel.cursor.style.display = "none";
    }
  }

  public static syncCursorIndicator(
    mousePositionOverViewPortContent: IPoint = EditorModel.mousePositionOnViewPortContent
  ) {
    if (!EditorModel.image || !EditorModel.canvas || !mousePositionOverViewPortContent) {
      EditorActions.hideCursorIndicator();
      return;
    }

    const viewPortContentScrollPosition: IPoint =
      ViewPortActions.getAbsoluteScrollPosition();
    const viewPortContentImageRect: IRect =
      ViewPortActions.calculateViewPortContentImageRect();
    const mousePositionOverViewPort: IPoint = PointUtil.subtract(
      mousePositionOverViewPortContent,
      viewPortContentScrollPosition
    );
    const isMouseOverImage: boolean = RectUtil.isPointInside(
      viewPortContentImageRect,
      mousePositionOverViewPortContent
    );
    const isMouseOverViewPort: boolean = RectUtil.isPointInside(
      { x: 0, y: 0, ...EditorModel.viewPortSize },
      mousePositionOverViewPort
    );

    if (
      isMouseOverViewPort &&
      !GeneralSelector.getPreventCustomCursorStatus()
    ) {
      EditorModel.cursor.style.left = mousePositionOverViewPort.x + "px";
      EditorModel.cursor.style.top = mousePositionOverViewPort.y + "px";
      EditorModel.cursor.style.display = "block";

      if (
        isMouseOverImage &&
        ![CustomCursorStyle.GRAB, CustomCursorStyle.GRABBING].includes(
          GeneralSelector.getCustomCursorStyle()
        )
      ) {
        const imageSize: ISize = ImageUtil.getSize(EditorModel.image);
        const scale: number = imageSize.width / viewPortContentImageRect.width;
        const mousePositionOverImage: IPoint = PointUtil.multiply(
          PointUtil.subtract(
            mousePositionOverViewPortContent,
            viewPortContentImageRect
          ),
          scale
        );
        const text = `x: ${Math.round(mousePositionOverImage.x)}, y: ${Math.round(
          mousePositionOverImage.y
        )}`;
        EditorModel.mousePositionIndicator.innerHTML = text;
        EditorModel.mousePositionIndicator.style.left =
          mousePositionOverViewPort.x + 15 + "px";
        EditorModel.mousePositionIndicator.style.top =
          mousePositionOverViewPort.y + 15 + "px";
        EditorModel.mousePositionIndicator.style.display = "block";
      } else {
        EditorModel.mousePositionIndicator.style.display = "none";
      }
    } else {
      EditorActions.hideCursorIndicator();
    }
  }
}
